import qrcode from "qrcode-generator";

// ---- numbers and links ----
// A phone number as WhatsApp wants it: digits only, with the country code (no +, no spaces). Returns "" when it cannot be a number.
export function waDigits(value) {
  let digits = String(value || "").replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  return digits.length >= 7 && digits.length <= 15 ? digits : "";
}

export const waLink = (number, text) => {
  const digits = waDigits(number);
  return digits ? `https://wa.me/${digits}${text ? `?text=${encodeURIComponent(text)}` : ""}` : "";
};

// "923001234567" -> "+92 300 1234567" (only to be read by people)
export function prettyNumber(digits) {
  const d = waDigits(digits);
  if (!d) return "";
  return `+${d.slice(0, d.length > 10 ? d.length - 10 : 2)} ${d.slice(d.length > 10 ? d.length - 10 : 2)}`;
}

// ---- a QR code of the link, as SVG (made in the browser: the number is not sent anywhere) ----
export function qrSvg(text, { size = 220, dark = "#111827", light = "#ffffff" } = {}) {
  const qr = qrcode(0, "M");
  qr.addData(String(text));
  qr.make();
  const count = qr.getModuleCount();
  const quiet = 3;
  const total = count + quiet * 2;
  let path = "";
  for (let r = 0; r < count; r += 1) for (let c = 0; c < count; c += 1) if (qr.isDark(r, c)) path += `M${c + quiet},${r + quiet}h1v1h-1z`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${total} ${total}" width="${size}" height="${size}" shape-rendering="crispEdges"><rect width="${total}" height="${total}" fill="${light}"/><path d="${path}" fill="${dark}"/></svg>`;
}

// ---- how WhatsApp shows a message: *bold*, _italic_, ~strike~ and ```code``` ----
export function waFormat(text) {
  const out = [];
  const pattern = /```([^`]+)```|\*([^*\n]+)\*|_([^_\n]+)_|~([^~\n]+)~/g;
  const value = String(text || "");
  let last = 0;
  let m = pattern.exec(value);
  while (m) {
    if (m.index > last) out.push({ kind: "text", text: value.slice(last, m.index) });
    if (m[1] !== undefined) out.push({ kind: "code", text: m[1] });
    else if (m[2] !== undefined) out.push({ kind: "bold", text: m[2] });
    else if (m[3] !== undefined) out.push({ kind: "italic", text: m[3] });
    else out.push({ kind: "strike", text: m[4] });
    last = m.index + m[0].length;
    m = pattern.exec(value);
  }
  if (last < value.length) out.push({ kind: "text", text: value.slice(last) });
  return out;
}

// The chat text of the website (**bold**, "- " lists) as WhatsApp text: the same conversion the server makes before it sends.
export function toWhatsApp(text) {
  return String(text || "").replace(/\r/g, "")
    .replace(/^\s{0,3}#{1,6}\s*(.+?)\s*#*$/gm, "*$1*")
    .replace(/\*\*(.+?)\*\*/g, "*$1*")
    .replace(/^(\s*)[-*]\s+/gm, "$1• ")
    .replace(/\n{3,}/g, "\n\n").trim();
}

// ---- what a website owner pastes ----
export function clickToChatSnippets({ number, text, color = "#25d366", label = "Chat on WhatsApp" }) {
  const link = waLink(number, text);
  if (!link) return { link: "", button: "", floating: "" };
  return {
    link,
    button: `<a href="${link}" target="_blank" rel="noopener" style="display:inline-block;background:${color};color:#fff;padding:12px 22px;border-radius:999px;font-family:sans-serif;font-weight:600;text-decoration:none">${label}</a>`,
    floating: `<a href="${link}" target="_blank" rel="noopener" aria-label="${label}" style="position:fixed;right:18px;bottom:18px;z-index:9999;width:58px;height:58px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;box-shadow:0 6px 20px rgba(0,0,0,.3)">\n  <svg viewBox="0 0 32 32" width="30" height="30" fill="#fff"><path d="M5 4h22a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H13l-6 6v-6H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/></svg>\n</a>`,
  };
}

export function webhookAddress(base, publicId = "YOUR_SECRET") {
  return `${String(base).replace(/\/+$/, "")}/core/channels/whatsapp/${publicId}/`;
}

export const META_STEPS = [
  ["Create a Meta app", "On developers.facebook.com make an app of type Business and add the WhatsApp product. Meta gives you a free test number straight away."],
  ["Copy three values", "From WhatsApp > API Setup copy the Phone number ID. In App settings > Basic copy the App secret. Make a permanent access token (System user in Business settings) with the whatsapp_business_messaging permission."],
  ["Add the channel here", "Pick the chatbot, choose WhatsApp (Meta), and paste the phone number ID, the access token and the app secret. Save: you get a webhook address and a verify token."],
  ["Give Meta the webhook", "In WhatsApp > Configuration press Edit, paste the webhook address and the verify token, then Verify and save. Subscribe to the \"messages\" field."],
  ["Test it", "Press Test connection here, then Send test message to your own number. Write to the number from your phone: the assistant answers."],
  ["Go live", "Add your real number in Meta, finish business verification, and enter its phone number ID here. Anyone can then write to it."],
];
export const TWILIO_STEPS = [
  ["Open the Twilio WhatsApp sandbox", "In the Twilio console go to Messaging > Try it out > Send a WhatsApp message. Join the sandbox from your phone with the code Twilio shows."],
  ["Copy two values", "From the Twilio console home copy the Account SID and the Auth token."],
  ["Add the channel here", "Choose WhatsApp (Twilio), paste the Account SID and the auth token, and enter the sandbox number (or your approved number). Save: you get a webhook address."],
  ["Give Twilio the webhook", "In the sandbox settings paste the webhook address in \"When a message comes in\" (method POST) and save."],
  ["Test it", "Press Test connection, then write to the sandbox number: the assistant answers."],
  ["Go live", "Request an approved WhatsApp sender in Twilio, then enter its number here."],
];

// ---- the real address of the API for a developer who wants to read what the assistant did ----
export const CHANNEL_NOTES = [
  "WhatsApp lets a business write freely only for 24 hours after the customer's last message. The assistant always answers inside that time; a message you write by hand later needs an approved template (not included).",
  "Meta charges per conversation after a free allowance; Twilio adds its own fee per message. These costs are paid to them, not to us.",
  "A number used with the WhatsApp Business Cloud API cannot also be used in the normal WhatsApp app. Use a new number, or Meta's test number.",
  "Voice notes are understood. Spoken answers need the Meta Cloud API.",
  "Photos, files and locations are not understood: the assistant asks the customer to type or send a voice note.",
];
